import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { trpc, createTRPCClient } from './lib/trpc';
import { ProjectProvider } from './context';
import { Layout } from './components/Layout';
import {
  DashboardPage,
  UsersPage,
  UserDetailPage,
  BotsPage,
  BotDetailPage,
  BotCreatePage,
  BotEditPage,
  BotOnboardingEditPage,
  TestBotPage,
  SafetyPage,
  AnalyticsPage,
  SettingsPage,
  SupportPage,
  MediaPage,
  CommunicationPlanPage,
  OnboardingPage,
  OnboardingFunnelPage,
  ToolsPage,
  ApiExpensesPage,
} from './pages';

/**
 * Root application component.
 *
 * Sets up the React Query client, tRPC provider, and routing.
 * All routes are wrapped with the Layout component for consistent navigation.
 */
export function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60, // 1 minute
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ProjectProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/:userId" element={<UserDetailPage />} />
                <Route path="bots" element={<BotsPage />} />
                <Route path="bots/new" element={<BotCreatePage />} />
                <Route path="bot/details" element={<BotDetailPage />} />
                <Route path="bot/test" element={<TestBotPage />} />
                <Route path="bot/edit" element={<BotEditPage />} />
                <Route path="onboarding" element={<OnboardingPage />}>
                  <Route index element={<Navigate to="editor" replace />} />
                  <Route path="editor" element={<BotOnboardingEditPage embedded />} />
                  <Route path="analytics" element={<OnboardingFunnelPage embedded />} />
                </Route>
                <Route path="bot/onboarding" element={<Navigate to="/onboarding/editor" replace />} />
                <Route path="safety" element={<SafetyPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="media" element={<MediaPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="api-expenses" element={<ApiExpensesPage />} />
                <Route path="onboarding-funnel" element={<Navigate to="/onboarding/analytics" replace />} />
                <Route path="communication-plan" element={<CommunicationPlanPage />} />
                <Route path="tools" element={<ToolsPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ProjectProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

export default App;
